/**
 * Narrows the shared Plan Actions menu to the items a surface declares, then
 * flattens it for AntD.
 *
 * Runs LAST in the menu pipeline (config sections -> toolbar overrides ->
 * `applyEditLockToMenu` -> here), because the `edit_plan` row is injected by the
 * edit-lock pass and an earlier filter could never see it.
 */
import type { MenuProps } from 'antd';

import type {
    PlanActionMenuItem,
    PlanActionMenuSections,
    PlanActionMenuViews,
    PlanActionSurface,
} from '../../config/planActionMenuConfig';
import { flattenMenuSections } from './menuSections';

const keyOf = (item: PlanActionMenuItem): string | undefined => {
    const key = item && 'key' in item ? item.key : undefined;
    return typeof key === 'string' ? key : undefined;
};

/**
 * Keeps the items `views` maps onto `surface`. A key absent from the map is
 * kept, so a customer config that predates the map renders the whole menu.
 *
 * A section emptied by the filter drops out when the sections are flattened, so
 * a view that hides a whole group loses its divider with it.
 */
export function buildPlanActionMenu(
    sections: PlanActionMenuSections,
    surface: PlanActionSurface,
    views: PlanActionMenuViews,
): MenuProps['items'] {
    const visible = sections.map((section) =>
        section.filter((item) => {
            const key = keyOf(item);
            if (key === undefined) return true;
            const surfaces = views[key];
            return surfaces === undefined || surfaces.includes(surface);
        }),
    );

    return flattenMenuSections(visible);
}
